import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';

const DAY_FULL = {
  sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
  thu: 'Thursday', fri: 'Friday', sat: 'Saturday',
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
  </div>
);

const StandingOrderDetails = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();

  const [order, setOrder]           = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { fetchOrder(); }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/admin/standing-orders/${id}`);
      if (response.data?.success) {
        setOrder(response.data.data.order);
        setOccurrences(response.data.data.occurrences || []);
      }
    } catch (error) {
      toast.error('Failed to fetch standing order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this standing order? All upcoming occurrences will be cancelled.')) {
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/admin/standing-orders/${id}/cancel`);
      toast.success('Standing order cancelled');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!order)  return <div className="text-gray-500 p-8">Order not found.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800">
            Standing Order SO-{order.standingOrderNumber}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Created {new Date(order.createdAt).toLocaleString()}
          </p>
        </div>
        <StatusBadge status={order.status} />
        {['active', 'paused'].includes(order.status) && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
          >
            {cancelling ? 'Cancelling…' : 'Cancel Series'}
          </button>
        )}
      </div>

      {/* Detail cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Series info */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">Series Details</h2>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Status"       value={order.status} />
            <Field label="Ride type"    value={order.rideType} />
            <Field label="Days"         value={(order.days || []).map((d) => DAY_FULL[d] || d).join(', ')} />
            <Field label="Time (Qatar)" value={order.time} />
            <Field label="Start date"   value={order.startDate ? new Date(order.startDate).toLocaleDateString() : '—'} />
            <Field label="End date"     value={order.endDate  ? new Date(order.endDate).toLocaleDateString()  : 'Open-ended'} />
            <Field label="Payment"      value={order.paymentMethod} />
            <Field label="Quoted fare"  value={order.quote?.totalFare != null ? `${order.quote.totalFare} ${order.quote.currency || 'QAR'}` : '—'} />
          </dl>
        </div>

        {/* Route */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">Route</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pickup</dt>
              <dd className="mt-1 text-sm text-gray-900">{order.pickup?.name || '—'}</dd>
              <dd className="text-xs text-gray-500">{order.pickup?.address}</dd>
              {order.pickup?.coordinates && (
                <dd className="text-xs text-gray-400">
                  {order.pickup.coordinates.latitude?.toFixed(5)},&nbsp;
                  {order.pickup.coordinates.longitude?.toFixed(5)}
                </dd>
              )}
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Dropoff</dt>
              <dd className="mt-1 text-sm text-gray-900">{order.dropoff?.name || '—'}</dd>
              <dd className="text-xs text-gray-500">{order.dropoff?.address}</dd>
              {order.dropoff?.coordinates && (
                <dd className="text-xs text-gray-400">
                  {order.dropoff.coordinates.latitude?.toFixed(5)},&nbsp;
                  {order.dropoff.coordinates.longitude?.toFixed(5)}
                </dd>
              )}
            </div>
            {order.driverNotes && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Driver notes</dt>
                <dd className="mt-1 text-sm text-gray-900 italic">{order.driverNotes}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Rider */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">Rider</h2>
          {order.rider ? (
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Name"  value={`${order.rider.firstName} ${order.rider.lastName}`} />
              <Field label="Phone" value={order.rider.phone} />
              <div className="col-span-2">
                <Link
                  to={`/users/${order.rider._id}`}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  View rider profile →
                </Link>
              </div>
            </dl>
          ) : (
            <p className="text-gray-400 text-sm">Rider data not available</p>
          )}
        </div>

        {/* Driver */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">Committed Driver</h2>
          {order.committedDriver ? (
            <dl className="grid grid-cols-2 gap-4">
              <Field
                label="Name"
                value={`${order.committedDriver.firstName} ${order.committedDriver.lastName}`}
              />
              <Field label="Employee #" value={order.committedDriver.employeeNumber} />
              <Field
                label="Assignment"
                value={order.isSpecificDriverRequested ? 'Requested by rider' : 'Auto-selected'}
              />
              <div className="col-span-2">
                <Link
                  to={`/drivers/${order.committedDriver._id}`}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  View driver profile →
                </Link>
              </div>
            </dl>
          ) : (
            <p className="text-gray-400 text-sm">No driver assigned</p>
          )}
        </div>
      </div>

      {/* Occurrences table */}
      <div className="card space-y-4">
        <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
          Recent Occurrences ({occurrences.length})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Ride ID</th>
                <th className="table-header">Date (Qatar)</th>
                <th className="table-header">Scheduled At</th>
                <th className="table-header">Fare</th>
                <th className="table-header">Status</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {occurrences.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-gray-400 py-6">
                    No occurrences materialized yet
                  </td>
                </tr>
              )}
              {occurrences.map((occ) => (
                <tr key={occ._id} className="hover:bg-gray-50">
                  <td className="table-cell font-mono text-sm">#{occ._id.slice(-6)}</td>
                  <td className="table-cell text-sm">
                    {occ.occurrenceDate
                      ? new Date(occ.occurrenceDate).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="table-cell text-sm text-gray-600">
                    {occ.scheduledAt
                      ? new Date(occ.scheduledAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="table-cell text-sm font-medium">
                    {occ.fare?.totalFare != null
                      ? `${occ.fare.totalFare} ${occ.fare.currency || 'QAR'}`
                      : '—'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={occ.status} />
                  </td>
                  <td className="table-cell">
                    <Link
                      to={`/rides/${occ._id}`}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      View ride
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StandingOrderDetails;
